import * as React from 'react';
import { Check, ChevronsUpDown, Loader2, X } from 'lucide-react';

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/command';
import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import { cn } from '@Utils/cn';

type ComboboxItem = {
  value: string;
  label: string;
};

interface MultiSelectComboboxProps {
  items: ComboboxItem[];
  selectedValues: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  loading?: boolean;
  maxSelected?: number;
  className?: string;
  footerHint?: string;
  inModal?: boolean;
}

export function MultiSelectCombobox({
  items,
  selectedValues,
  onChange,
  placeholder = 'Select items...',
  disabled,
  loading = false,
  maxSelected,
  className,
  footerHint,
  inModal,
}: MultiSelectComboboxProps) {
  const [open, setOpen] = React.useState(false);

  const byValue = React.useMemo(() => {
    const m = new Map<string, ComboboxItem>();
    items.forEach((item) => m.set(item.value, item));
    return m;
  }, [items]);

  const selectedItems: ComboboxItem[] = (selectedValues ?? [])
    .map((val) => byValue.get(val))
    .filter(Boolean) as ComboboxItem[];

  const toggle = (value: string) => {
    const exists = selectedValues.includes(value);
    if (exists) {
      onChange(selectedValues.filter((v) => v !== value));
    } else {
      if (maxSelected && selectedValues.length >= maxSelected) return;
      onChange([...selectedValues, value]);
    }
  };

  const clearAll = () => onChange([]);

  return (
    <div className={cn('space-y-2', className)}>
      <Popover open={open} onOpenChange={setOpen} modal={inModal}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={cn(
              'w-full justify-between',
              !selectedItems.length && 'text-muted-foreground',
            )}
            disabled={disabled || loading}
          >
            <span className="truncate">
              {loading
                ? 'Loading...'
                : selectedItems.length
                  ? `${selectedItems.length} selected`
                  : placeholder}
            </span>
            <span className="inline-flex items-center gap-1">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              <ChevronsUpDown className="h-4 w-4 opacity-50" />
            </span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0">
          <Command shouldFilter>
            <CommandInput placeholder="Search..." />
            <CommandList>
              <CommandEmpty>
                {loading ? (
                  <div className="flex w-full items-center justify-center p-4">
                    <Loader2 className="h-4 w-4 animate-spin text-gray-500" />
                  </div>
                ) : (
                  'No items found.'
                )}
              </CommandEmpty>
              <CommandGroup>
                {selectedItems.length > 0 ? (
                  <CommandItem
                    value="__clear__"
                    className="text-muted-foreground"
                    onSelect={() => clearAll()}
                  >
                    Clear all
                  </CommandItem>
                ) : null}

                {!loading &&
                  items.map((item) => {
                    const selected = selectedValues.includes(item.value);
                    return (
                      <CommandItem
                        key={item.value}
                        value={`${item.value}|${item.label}`}
                        onSelect={() => toggle(item.value)}
                      >
                        <Check
                          className={cn(
                            'mr-2 h-4 w-4',
                            selected ? 'opacity-100' : 'opacity-0',
                          )}
                        />
                        <span className="truncate">{item.label}</span>
                      </CommandItem>
                    );
                  })}

                {footerHint && !loading && items.length > 0 && (
                  <div className="text-muted-foreground px-2 py-1.5 text-xs italic">
                    {footerHint}
                  </div>
                )}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {selectedItems.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {selectedItems.map((item) => (
            <span
              key={item.value}
              className="inline-flex items-center gap-1 rounded-full border px-2 py-1 text-xs"
            >
              {item.label}
              <button
                type="button"
                aria-label={`Remove ${item.label}`}
                onClick={() => toggle(item.value)}
                className="hover:bg-muted rounded-full p-0.5"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}
